import { styled } from "styled-components";

import { PaperAirplaneIcon } from "@vericus/cadmus-icons";
import { Alert, Button, Modal, Text, VisuallyHidden } from "@vericus/cadmus-ui";

import { create, useModal } from "@ebay/nice-modal-react";

import { LoadingQuill } from "@/components/loading-quill";
import { useAppDispatch, useAppSelector } from "@/data/hooks";
import {
  clearError,
  hidePreview,
  selectIsSubmitting,
  selectSubmitError,
  SubmitError,
} from "@/features/authority";

interface Props {
  /**
   * Callback to manually retry the current submit request, if allowed.
   */
  onRetry?: () => void;

  /**
   * The modal is for an un-submission request.
   */
  isUnsubmit?: boolean;

  /**
   * The modal is triggered via an auto-submission.
   */
  autoSubmission?: boolean;
}

/**
 * Stateful Modal which tracks the submit async action.
 */
export const SubmitStatusModal = create((props: Props) => {
  const modal = useModal();

  const dispatch = useAppDispatch();
  const isSubmitting = useAppSelector(selectIsSubmitting);
  const submitError = useAppSelector(selectSubmitError);

  const onDismissError = () => {
    dispatch(hidePreview());
    dispatch(clearError());
    modal.hide();
  };

  return (
    <SubmitStatusModalContent
      {...props}
      visible={modal.visible}
      onExitComplete={modal.remove}
      isSubmitting={isSubmitting}
      error={submitError}
      onDismissError={onDismissError}
    />
  );
});

// Inner modal props
interface ModalProps extends Props {
  /** Modal visibility state. */
  visible: boolean;
  /** Callback when modal exit animation is complete. */
  onExitComplete?: VoidFunction;
  /** Submit request is in-flight. */
  isSubmitting: boolean;
  /** Submission error. */
  error: SubmitError | null;
  /** Submission process is triggered by an auto-submission. */
  autoSubmission?: boolean;
  /**
   * Callback to dismiss submission errors.
   */
  onDismissError?: () => void;
}

// Inner modal content
export function SubmitStatusModalContent(props: ModalProps) {
  const {
    onRetry,
    visible,
    onExitComplete,
    isSubmitting,
    error,
    autoSubmission = false,
    isUnsubmit = false,
    onDismissError,
  } = props;

  const loadingMessage = isUnsubmit ? "Preparing..." : "Submitting...";

  let content = null;

  const hasNetworkError = error !== null && error.kind === "network";
  const hasValidationError = error !== null && error.kind === "validation";
  const hasConflictError = error !== null && error.kind === "already_reported";

  if (hasNetworkError && autoSubmission) {
    content = (
      <Root tabIndex={0}>
        <Modal.Title asChild>
          <Text kind="headingOne">{"Time's"} up!</Text>
        </Modal.Title>
        <Alert severity="warning" title="Your writing time has ended">
          Please keep this window open. Your work will be auto submitted once
          you are reconnected to the internet. Contact your teacher if you’re
          unable to connect to the internet again.
        </Alert>
        <div>
          <Button
            kind="primary"
            size="lg"
            rightIcon={<PaperAirplaneIcon size={24} label="" />}
            disabled={!onRetry || isSubmitting}
            onClick={onRetry}
            style={{ width: 336 }}
          >
            {!onRetry ? "Retrying submission..." : "Retry now"}
          </Button>
        </div>
      </Root>
    );
  } else if (hasNetworkError) {
    content = (
      <Root>
        <Modal.Title asChild>
          <Text kind="headingOne">Unable to submit</Text>
        </Modal.Title>
        <Alert severity="error" title="Network disruption">
          Please check your internet connection. Your work will be submitted
          once you are reconnected to the internet.
        </Alert>
        <div>
          <Button
            kind="primary"
            size="lg"
            rightIcon={<PaperAirplaneIcon size={24} label="" />}
            disabled={!onRetry || isSubmitting}
            onClick={onRetry}
            style={{ width: 336 }}
          >
            {!onRetry ? "Retrying submission..." : "Retry now"}
          </Button>
        </div>
      </Root>
    );
  } else if (hasValidationError) {
    content = (
      <Root>
        <Modal.Title asChild>
          <Text kind="headingOne">Unable to submit</Text>
        </Modal.Title>
        {error.detail && (
          <Alert severity="warning" title={error.title}>
            {error.detail}
          </Alert>
        )}
        <div>
          {onDismissError && (
            <Button
              kind="primary"
              size="lg"
              disabled={isSubmitting}
              onClick={onDismissError}
              style={{ width: 336 }}
            >
              Continue Working
            </Button>
          )}
        </div>
      </Root>
    );
  } else if (hasConflictError) {
    content = (
      <Root>
        <Modal.Title asChild>
          <Text kind="headingOne">Duplicate submission</Text>
        </Modal.Title>
        <Alert severity="warning" title="Submission attempt already received">
          Please reload the page to see your latest submission.
        </Alert>
        <div>
          {onDismissError && (
            <Button
              kind="primary"
              size="lg"
              disabled={isSubmitting}
              onClick={() => {
                window.location.reload();
              }}
              style={{ width: 336 }}
            >
              See latest submission
            </Button>
          )}
        </div>
      </Root>
    );
  } else {
    content = (
      <Root tabIndex={0}>
        <VisuallyHidden.Root>
          <Modal.Title>Loading {loadingMessage}...</Modal.Title>
        </VisuallyHidden.Root>
        <LoadingQuill message={loadingMessage} />
      </Root>
    );
  }

  return (
    <Modal.Root open={visible}>
      <Modal.Content withCloseButton={false} onExitComplete={onExitComplete}>
        <VisuallyHidden.Root>
          <Modal.Description>
            Modal describing the current submission status
          </Modal.Description>
        </VisuallyHidden.Root>
        {content}
      </Modal.Content>
    </Modal.Root>
  );
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 32px;

  padding: 88px 48px;
  box-sizing: border-box;
  width: 636px;
  max-width: 98vw;
  max-height: 80vh;
  min-height: 438px;

  &:focus {
    outline: 0;
  }
`;
